import { GROCERY_API_PATHS } from '../constants/apiPaths';
import axiosGroceryInstance from '../restaurant/AxiosGroceryInstance';
import { mapRestaurantUserRow } from '../restaurant/restaurantApi';

export const getAuthHeaders = () => {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const extractStoreId = (item) =>
  item?.storeId ?? item?.Store_Id ?? item?.store_id ?? item?.id ?? null;

export const extractStoreName = (item) =>
  item?.storeName ?? item?.Store_Name ?? item?.name ?? '';

export const normalizeAddressesForTable = (store) => {
  const addresses = store?.addresses;
  if (!Array.isArray(addresses)) return [];
  return addresses.map((addr) => ({
    addressId: addr.addressId ?? addr.Address_Id ?? addr.address_id,
    addressLine1: addr.addressLine1 ?? addr.Address_Line1 ?? '',
    addressLine2: addr.addressLine2 ?? addr.Address_Line2 ?? '',
    areaName: addr.areaName ?? addr.Area_Name ?? '',
    city: addr.city ?? addr.City ?? '',
    state: addr.state ?? addr.State ?? '',
    postalCode: addr.postalCode ?? addr.Postal_Code ?? '',
    country: addr.country ?? addr.Country ?? '',
    lat: addr.lat ?? addr.Lat ?? '',
    lon: addr.lon ?? addr.Lon ?? '',
    homeDeliveryAvailable:
      addr.homeDeliveryAvailable ?? addr.Home_Delivery_Available ?? false,
  }));
};

export const fetchStoreById = async (storeId) => {
  const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.storeById(storeId), {
    headers: getAuthHeaders(),
  });
  const data = response.data;
  return {
    ...data,
    addresses: normalizeAddressesForTable(data),
  };
};

export const fetchStoreRootAdmin = async (storeId) => {
  const response = await axiosGroceryInstance.get(
    GROCERY_API_PATHS.storeRootAdminByStoreId(storeId),
    { headers: getAuthHeaders() }
  );
  const owner = response.data?.owner;
  if (!owner || typeof owner !== 'object') return null;
  return mapRestaurantUserRow(owner);
};

export const listStores = async (params = {}) => {
  const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.stores, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data?.stores || [];
};

export const updateStore = async (storeId, form, updatedById) => {
  const contactNo = form.contactNo
    ? parseInt(String(form.contactNo).replace(/\D/g, ''), 10) || form.contactNo
    : form.contactNo;

  const response = await axiosGroceryInstance.put(
    GROCERY_API_PATHS.storeByIdUpdate(storeId),
    {
      storeName: form.storeName,
      shortCode: String(form.shortCode || '').trim().toUpperCase(),
      ...(form.licenseNumber !== undefined
        ? { licenseNumber: String(form.licenseNumber || '').replace(/\s+/g, '').toUpperCase() }
        : {}),
      contactNo,
      email: form.email,
      notes: form.notes,
      homeDeliveryAvailable:
        form.homeDeliveryAvailable === true ||
        form.homeDeliveryAvailable === 'true' ||
        form.homeDeliveryAvailable === '1',
      isDefault:
        form.isDefault === true ||
        form.isDefault === 'true' ||
        form.isDefault === '1' ||
        form.isDefault === 1,
      ...(form.status != null && String(form.status).trim() !== ''
        ? { status: String(form.status).trim().toUpperCase() }
        : {}),
      updatedBy: updatedById ?? 1,
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateStoreAddress = async (addressId, address, updatedById) => {
  const lat = parseFloat(address.lat);
  const lon = parseFloat(address.lon);
  const response = await axiosGroceryInstance.put(
    GROCERY_API_PATHS.storeAddressById(addressId),
    {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      areaName: address.areaName,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      lat: Number.isFinite(lat) ? lat : undefined,
      lon: Number.isFinite(lon) ? lon : undefined,
      homeDeliveryAvailable: address.homeDeliveryAvailable,
      updatedBy: updatedById ?? 1,
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteStore = async (storeId) => {
  const response = await axiosGroceryInstance.delete(
    GROCERY_API_PATHS.storeByIdUpdate(storeId),
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const fetchStoreMyeatsDeliverySlots = async (storeId) => {
  const response = await axiosGroceryInstance.get(
    GROCERY_API_PATHS.storeMyeatsDeliverySlots(storeId),
    { headers: getAuthHeaders() }
  );
  return response.data ?? {};
};

export const updateStoreMyeatsDeliverySlots = async (storeId, payload, userId) => {
  const response = await axiosGroceryInstance.put(
    GROCERY_API_PATHS.storeMyeatsDeliverySlots(storeId),
    {
      ...payload,
      updatedById: userId,
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const fetchStoreTimings = async (storeId) => {
  const response = await axiosGroceryInstance.get(
    GROCERY_API_PATHS.storeTimings(storeId),
    { headers: getAuthHeaders() }
  );
  return response.data ?? {};
};

export const updateStoreTimings = async (storeId, payload, userId) => {
  const response = await axiosGroceryInstance.put(
    GROCERY_API_PATHS.storeTimings(storeId),
    {
      ...payload,
      updatedById: userId,
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};
